/**
 * InquiriesPage — Super Admin: Demo Requests + Contact Messages
 * Route: /superadmin/inquiries  (add to App.js with SA role)
 * Add to DashboardLayout nav for super-admin role
 *
 * FIXES:
 * 1. Reply tab: client-side validation before hitting the API
 *    - responseText required (non-empty)
 *    - scheduleDemoLink validated as a valid URL if provided (demo type only)
 *    - scheduleDemoDate validated: must not be in the past (demo type only)
 *    - Inline error messages shown under each field
 *    - Send button disabled while fields are invalid OR while sending
 * 2. Error message from API shown in toast (was already there, kept)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Modal, Form,
    Row, Col, InputGroup, Spinner, Alert, Tabs, Tab,
} from 'react-bootstrap';
import {
    MessageSquare, Mail, Phone, Building2, Calendar,
    Eye, Send, RefreshCw, Search, CheckCircle,
    Clock, TrendingUp, User, X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-IN',
    { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
const STATUS_CFG = {
    new:            { bg:'danger',    text:'white', label:'New',            icon:'🆕' },
    read:           { bg:'secondary', text:'white', label:'Read',           icon:'👁' },
    contacted:      { bg:'primary',   text:'white', label:'Contacted',      icon:'📧' },
    demo_scheduled: { bg:'info',      text:'white', label:'Demo Scheduled', icon:'📅' },
    converted:      { bg:'success',   text:'white', label:'Converted ✓',   icon:'🎉' },
    closed:         { bg:'dark',      text:'white', label:'Closed',         icon:'🔒' },
};
const STATUS_OPTIONS = [
    {value:'new',label:'New'},
    {value:'read',label:'Read'},
    {value:'contacted',label:'Contacted'},
    {value:'demo_scheduled',label:'Demo Scheduled'},
    {value:'converted',label:'Converted'},
    {value:'closed',label:'Closed'},
];

// ── Reply form validation ─────────────────────────────────────────────────────
const isValidUrl = (url) => {
    if (!url) return true; // optional field
    try { new URL(url); return true; } catch { return false; }
};

const validateReply = ({ responseText, scheduleDemoLink, scheduleDemoDate, isDemo }) => {
    const errs = {};
    if (!responseText.trim())
        errs.responseText = 'Response message is required before sending.';
    else if (responseText.trim().length < 20)
        errs.responseText = 'Response is too short — please write at least 20 characters.';

    if (isDemo) {
        if (scheduleDemoLink && !isValidUrl(scheduleDemoLink))
            errs.scheduleDemoLink = 'Enter a valid URL (e.g. https://calendly.com/…)';

        if (scheduleDemoDate) {
            const selected = new Date(scheduleDemoDate);
            if (selected <= new Date())
                errs.scheduleDemoDate = 'Demo date must be in the future.';
        }
    }
    return errs;
};
// ════════════════════════════════════════════════════════════════════════════
const InquiriesPage = () => {
    const [items,      setItems]      = useState([]);
    const [total,      setTotal]      = useState(0);
    const [counts,     setCounts]     = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [selected,   setSelected]   = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    // Filters
    const [typeFilter,   setTypeFilter]   = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search,       setSearch]       = useState('');
    const [page,         setPage]         = useState(1);
    const LIMIT = 20;
    // Response form
    const [responseText,     setResponseText]     = useState('');
    const [scheduleDemoLink, setScheduleDemoLink] = useState('');
    const [scheduleDemoDate, setScheduleDemoDate] = useState('');
    const [sendingReply,     setSendingReply]     = useState(false);
    const [updatingStatus,   setUpdatingStatus]   = useState(false);
    // Validation errors for reply form
    const [replyErrors, setReplyErrors] = useState({});
    const [replyAttempted, setReplyAttempted] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/superadmin/inquiries', {
                params: {
                    type:   typeFilter   || undefined,
                    status: statusFilter || undefined,
                    search: search       || undefined,
                    page, limit: LIMIT,
                },
            });
            const data = res.data.data;
            setItems(data?.items || []);
            setTotal(data?.total || 0);
            setCounts(data?.counts || []);
        } catch { toast.error('Failed to load inquiries'); }
        finally { setLoading(false); }
    }, [typeFilter, statusFilter, search, page]);
    useEffect(() => { load(); }, [load]);
    // Auto-refresh every 60s
    useEffect(() => {
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load]);

    const openDetail = async (item) => {
        try {
            const res = await axiosInstance.get(`/superadmin/inquiries/${item.id}`);
            setSelected(res.data.data);
            setResponseText('');
            setScheduleDemoLink('');
            setScheduleDemoDate('');
            setReplyErrors({});
            setReplyAttempted(false);
            setShowDetail(true);
            load();
        } catch { toast.error('Failed to load inquiry'); }
    };

    // Re-validate in real time once user has attempted to submit
    useEffect(() => {
        if (!replyAttempted || !selected) return;
        const errs = validateReply({
            responseText, scheduleDemoLink, scheduleDemoDate,
            isDemo: selected?.type === 'demo',
        });
        setReplyErrors(errs);
    }, [responseText, scheduleDemoLink, scheduleDemoDate, replyAttempted, selected]);

    const sendResponse = async () => {
        setReplyAttempted(true);
        const errs = validateReply({
            responseText, scheduleDemoLink, scheduleDemoDate,
            isDemo: selected?.type === 'demo',
        });
        setReplyErrors(errs);
        if (Object.keys(errs).length > 0) return; // stop — show inline errors

        setSendingReply(true);
        try {
            await axiosInstance.post(`/superadmin/inquiries/${selected.id}/respond`, {
                response_text:      responseText.trim(),
                schedule_demo_link: scheduleDemoLink || undefined,
                schedule_demo_date: scheduleDemoDate || undefined,
            });
            toast.success(`Response sent to ${selected.name} (${selected.email})`);
            setSelected(p => ({ ...p, status:'contacted', response_text: responseText }));
            setReplyErrors({});
            setReplyAttempted(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send response');
        } finally { setSendingReply(false); }
    };

    const updateStatus = async (id, status) => {
        setUpdatingStatus(true);
        try {
            await axiosInstance.patch(`/superadmin/inquiries/${id}/status`, { status });
            toast.success('Status updated');
            if (selected?.id === id) setSelected(p => ({ ...p, status }));
            load();
        } catch (err) { toast.error('Failed to update status'); }
        finally { setUpdatingStatus(false); }
    };
    // Counts helpers
    const countFor = (type, status) =>
        counts.find(c => c.type === type && c.status === status)?.cnt || 0;
    const newDemos    = countFor('demo', 'new');
    const newContacts = countFor('contact', 'new');
    const totalPages = Math.ceil(total / LIMIT);
    // Derived: is the reply form valid right now?
    const replyFormValid = selected && responseText.trim().length >= 20
        && (!scheduleDemoLink || isValidUrl(scheduleDemoLink))
        && (!scheduleDemoDate || new Date(scheduleDemoDate) > new Date());

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <MessageSquare size={22} className="text-primary"/>
                        Inquiries & Demo Requests
                        {(newDemos + newContacts) > 0 && (
                            <Badge bg="danger" className="ms-1">
                                {newDemos + newContacts} new
                            </Badge>
                        )}
                    </h4>
                    <p className="text-muted small mb-0">
                        Demo requests and contact form submissions from your landing page
                    </p>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={13}/>
                </Button>
            </div>
            {/* Summary cards */}
            <Row className="g-3 mb-4">
                {[
                    { label:'Demo Requests',    value: counts.filter(c=>c.type==='demo').reduce((a,c)=>a+parseInt(c.cnt||0),0),     icon:'🎯', color:'#4f46e5', new: newDemos    },
                    { label:'Contact Messages', value: counts.filter(c=>c.type==='contact').reduce((a,c)=>a+parseInt(c.cnt||0),0),  icon:'💬', color:'#0891b2', new: newContacts  },
                    { label:'Converted',        value: counts.filter(c=>c.status==='converted').reduce((a,c)=>a+parseInt(c.cnt||0),0), icon:'🎉', color:'#059669', new: 0 },
                    { label:'Total Inquiries',  value: counts.reduce((a,c)=>a+parseInt(c.cnt||0),0),                                icon:'📊', color:'#7c3aed', new: 0 },
                ].map(({ label, value, icon, color, new: n }) => (
                    <Col xs={6} md={3} key={label}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="text-muted small">{label}</div>
                                        <div style={{ fontSize:28, fontWeight:800, color }}>{value}</div>
                                    </div>
                                    <div style={{ fontSize:28 }}>{icon}</div>
                                </div>
                                {n > 0 && (
                                    <Badge bg="danger" style={{fontSize:10}}>
                                        {n} unread
                                    </Badge>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4 p-3">
                <Row className="g-2 align-items-center">
                    <Col xs={12} md={4}>
                        <InputGroup size="sm">
                            <InputGroup.Text><Search size={12}/></InputGroup.Text>
                            <Form.Control placeholder="Search name, email, company…"
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}/>
                            {search && (
                                <Button variant="outline-secondary"
                                    onClick={() => { setSearch(''); setPage(1); }}>
                                    <X size={12}/>
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Select size="sm" value={typeFilter}
                            onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                            <option value="">All Types</option>
                            <option value="demo">Demo Requests</option>
                            <option value="contact">Contact Messages</option>
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Select size="sm" value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                            <option value="">All Statuses</option>
                            {STATUS_OPTIONS.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={4} className="text-end">
                        <span className="text-muted small">
                            {total} total · Page {page} of {totalPages || 1}
                        </span>
                    </Col>
                </Row>
            </Card>
            {/* Table */}
            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary"/>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <MessageSquare size={36} className="mb-2 opacity-30"/>
                        <p>No inquiries found.</p>
                    </div>
                ) : (
                    <Table hover responsive className="mb-0 align-middle small">
                        <thead className="bg-light">
                            <tr className="text-uppercase text-muted" style={{fontSize:10}}>
                                <th className="ps-4">Type</th>
                                <th>Name & Company</th>
                                <th>Contact</th>
                                <th>Message</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th className="pe-4">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => {
                                const sc = STATUS_CFG[item.status] || STATUS_CFG.read;
                                const isNew = item.status === 'new';
                                return (
                                    <tr key={i}
                                        style={{
                                            background: isNew ? '#fff9f9' : '#fff',
                                            fontWeight: isNew ? 600 : 400,
                                        }}>
                                        <td className="ps-4">
                                            <Badge bg={item.type === 'demo' ? 'primary' : 'secondary'}
                                                style={{fontSize:10}}>
                                                {item.type === 'demo' ? '🎯 Demo' : '💬 Contact'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="fw-medium">{item.name}</div>
                                            <div className="text-muted" style={{fontSize:11}}>
                                                {item.company_name || '—'}
                                                {item.company_size ? ` · ${item.company_size}` : ''}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{fontSize:12}}>{item.email}</div>
                                            {item.phone && (
                                                <div className="text-muted" style={{fontSize:11}}>{item.phone}</div>
                                            )}
                                        </td>
                                        <td style={{maxWidth:200}}>
                                            <span className="text-muted" style={{fontSize:12}}>
                                                {(item.message || item.subject || '').slice(0, 60)}
                                                {(item.message || '').length > 60 ? '…' : ''}
                                            </span>
                                        </td>
                                        <td className="text-muted" style={{fontSize:11}}>
                                            {fmtDate(item.created_at)}
                                        </td>
                                        <td>
                                            <Badge bg={sc.bg} text={sc.text} style={{fontSize:10}}>
                                                {sc.icon} {sc.label}
                                            </Badge>
                                        </td>
                                        <td className="pe-4">
                                            <Button size="sm" variant="outline-primary"
                                                className="py-0 px-2"
                                                onClick={() => openDetail(item)}>
                                                <Eye size={13} className="me-1"/>View
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                        <span className="text-muted small">
                            Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)} of {total}
                        </span>
                        <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-secondary"
                                disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹</Button>
                            {[...Array(Math.min(5,totalPages))].map((_,i)=>{
                                const pg = Math.max(1,Math.min(page-2,totalPages-4))+i;
                                return (
                                    <Button key={pg} size="sm"
                                        variant={pg===page?'primary':'outline-secondary'}
                                        onClick={()=>setPage(pg)}>
                                        {pg}
                                    </Button>
                                );
                            })}
                            <Button size="sm" variant="outline-secondary"
                                disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>›</Button>
                        </div>
                    </div>
                )}
            </Card>
            {/* ── Detail / Respond Modal ────────────────────────────────── */}
            <Modal show={showDetail} onHide={()=>setShowDetail(false)}
                size="lg" centered>
                {selected && (
                    <>
                        <Modal.Header closeButton className="border-0">
                            <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                                <Badge bg={selected.type==='demo'?'primary':'secondary'}>
                                    {selected.type === 'demo' ? '🎯 Demo Request' : '💬 Contact Message'}
                                </Badge>
                                from {selected.name}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-4">
                            <Tabs defaultActiveKey="details" className="mb-4">
                                {/* ── Details tab ── */}
                                <Tab eventKey="details" title="Details">
                                    <Row className="g-3 mb-4">
                                        {[
                                            { icon:User,      label:'Name',          value: selected.name },
                                            { icon:Mail,      label:'Email',          value: selected.email },
                                            { icon:Phone,     label:'Phone',          value: selected.phone || 'Not provided' },
                                            { icon:Building2, label:'Company',        value: selected.company_name || 'Not provided' },
                                            { icon:User,      label:'Company Size',   value: selected.company_size || 'Not specified' },
                                            { icon:Building2, label:'Industry',       value: selected.industry || 'Not specified' },
                                            { icon:Calendar,  label:'Preferred Time', value: selected.preferred_time || 'No preference' },
                                            { icon:Clock,     label:'Received',       value: fmtDate(selected.created_at) },
                                        ].filter(r => r.value && r.value !== 'Not provided' && r.value !== 'Not specified').map(({ icon:Icon, label, value }) => (
                                            <Col xs={12} sm={6} key={label}>
                                                <div className="d-flex gap-2 align-items-start">
                                                    <div style={{
                                                        width:32, height:32, borderRadius:8,
                                                        background:'#eef2ff', flexShrink:0,
                                                        display:'flex', alignItems:'center', justifyContent:'center',
                                                    }}>
                                                        <Icon size={14} color="#4f46e5"/>
                                                    </div>
                                                    <div>
                                                        <div style={{fontSize:11,color:'#94a3b8'}}>{label}</div>
                                                        <div style={{fontSize:13,fontWeight:600}}>{value}</div>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                    {/* Message */}
                                    {(selected.message || selected.subject) && (
                                        <div className="p-3 rounded-3 mb-3"
                                            style={{background:'#f8fafc',borderLeft:'4px solid #4f46e5'}}>
                                            <div className="fw-bold small mb-2">
                                                {selected.subject ? `Subject: ${selected.subject}` : 'Message'}
                                            </div>
                                            <p className="small mb-0" style={{whiteSpace:'pre-wrap'}}>
                                                {selected.message}
                                            </p>
                                        </div>
                                    )}
                                    {/* Previous response */}
                                    {selected.response_text && (
                                        <Alert variant="success" className="small">
                                            <div className="fw-bold mb-1">
                                                ✅ Responded by {selected.responded_by_name} on {fmtDate(selected.responded_at)}
                                            </div>
                                            <p className="mb-0" style={{whiteSpace:'pre-wrap'}}>
                                                {selected.response_text}
                                            </p>
                                            {selected.schedule_demo_link && (
                                                <div className="mt-1">
                                                    📅 Demo link: <a href={selected.schedule_demo_link}
                                                        target="_blank" rel="noreferrer">
                                                        {selected.schedule_demo_link}
                                                    </a>
                                                </div>
                                            )}
                                        </Alert>
                                    )}
                                    {/* Status change */}
                                    <div className="d-flex align-items-center gap-2 mt-3">
                                        <span className="small fw-bold">Update Status:</span>
                                        <Form.Select size="sm" value={selected.status}
                                            onChange={e => updateStatus(selected.id, e.target.value)}
                                            disabled={updatingStatus}
                                            style={{width:'auto'}}>
                                            {STATUS_OPTIONS.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </Form.Select>
                                        {updatingStatus && <Spinner size="sm"/>}
                                    </div>
                                </Tab>

                                {/* ── Reply tab ── */}
                                <Tab eventKey="reply" title={`Reply to ${selected.name}`}>

                                    {/* Already responded banner */}
                                    {selected.response_text && (
                                        <Alert variant="info" className="small mb-3 py-2">
                                            ℹ️ You have already replied to this inquiry on {fmtDate(selected.responded_at)}.
                                            Sending again will overwrite the previous response record.
                                        </Alert>
                                    )}

                                    {/* Recipient info */}
                                    <div className="mb-3 p-3 rounded-3"
                                        style={{background:'#f8fafc',border:'1px solid #e5e7eb'}}>
                                        <div className="small text-muted mb-1">Sending to:</div>
                                        <div className="fw-bold">{selected.name}</div>
                                        <div className="small text-primary">{selected.email}</div>
                                    </div>

                                    {/* Response message */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">
                                            Response Message <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea" rows={6}
                                            placeholder={`Hi ${selected.name},\n\nThank you for reaching out to PeoplesDock!\n\n...`}
                                            value={responseText}
                                            onChange={e => setResponseText(e.target.value)}
                                            isInvalid={replyAttempted && !!replyErrors.responseText}
                                            isValid={replyAttempted && !replyErrors.responseText && responseText.trim().length >= 20}/>
                                        <Form.Control.Feedback type="invalid">
                                            {replyErrors.responseText}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            {responseText.trim().length} characters
                                            {responseText.trim().length > 0 && responseText.trim().length < 20
                                                ? ' — minimum 20 characters' : ''}
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Demo-only fields */}
                                    {selected.type === 'demo' && (
                                        <>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-bold">
                                                    Demo Scheduling Link
                                                    <span className="fw-normal text-muted ms-1">(optional)</span>
                                                </Form.Label>
                                                <Form.Control
                                                    placeholder="e.g. https://calendly.com/peoplesdock/demo"
                                                    value={scheduleDemoLink}
                                                    onChange={e => setScheduleDemoLink(e.target.value)}
                                                    isInvalid={replyAttempted && !!replyErrors.scheduleDemoLink}
                                                    isValid={replyAttempted && !replyErrors.scheduleDemoLink && !!scheduleDemoLink}/>
                                                <Form.Control.Feedback type="invalid">
                                                    {replyErrors.scheduleDemoLink}
                                                </Form.Control.Feedback>
                                                <Form.Text className="text-muted small">
                                                    Calendly, Google Meet, or any booking link
                                                </Form.Text>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-bold">
                                                    Proposed Demo Date/Time
                                                    <span className="fw-normal text-muted ms-1">(optional)</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    min={new Date(Date.now() + 60000).toISOString().slice(0,16)}
                                                    value={scheduleDemoDate}
                                                    onChange={e => setScheduleDemoDate(e.target.value)}
                                                    isInvalid={replyAttempted && !!replyErrors.scheduleDemoDate}
                                                    isValid={replyAttempted && !replyErrors.scheduleDemoDate && !!scheduleDemoDate}/>
                                                <Form.Control.Feedback type="invalid">
                                                    {replyErrors.scheduleDemoDate}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </>
                                    )}

                                    {/* Quick reply templates */}
                                    <div className="mb-3">
                                        <div className="small fw-bold text-muted mb-2">
                                            Quick Templates:
                                        </div>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {[
                                                { label: 'Demo Invite', text: `Hi ${selected.name},\n\nThank you for your interest in PeoplesDock! I'd love to schedule a personalized demo for ${selected.company_name || 'your company'}.\n\nOur demo covers:\n• Payroll automation (TDS, PF, ESI)\n• GPS attendance & biometric sync\n• AI HR Assistant\n• Compliance calendar\n\nWould you be available for a 30-minute demo this week? You can book a slot using the link below.\n\nLooking forward to speaking with you!\n\nBest regards,\nPeoplesDock Team` },
                                                { label: 'More Info',   text: `Hi ${selected.name},\n\nThank you for reaching out to PeoplesDock!\n\nPeoplesDock is a complete HR & payroll platform built for Indian companies. Plans start at ₹999/employee/month with a 14-day free trial.\n\nYou can explore everything at peoplesdock.com or I can arrange a quick call to answer your specific questions.\n\nBest regards,\nPeoplesDock Team` },
                                                { label: 'Follow Up',  text: `Hi ${selected.name},\n\nJust following up on your inquiry about PeoplesDock. Have you had a chance to look at our platform?\n\nIf you have any questions or would like to see a demo, I'm happy to help.\n\nBest regards,\nPeoplesDock Team` },
                                            ].map(t => (
                                                <Button key={t.label} size="sm" variant="outline-secondary"
                                                    onClick={() => {
                                                        setResponseText(t.text);
                                                        // Clear the responseText error immediately if it was showing
                                                        if (replyAttempted)
                                                            setReplyErrors(p => ({ ...p, responseText: '' }));
                                                    }}>
                                                    {t.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Summary of validation errors at top if any */}
                                    {replyAttempted && Object.keys(replyErrors).length > 0 && (
                                        <Alert variant="danger" className="small py-2 mb-3">
                                            Please fix the errors above before sending.
                                        </Alert>
                                    )}

                                    <Button
                                        variant="primary"
                                        onClick={sendResponse}
                                        disabled={sendingReply}
                                        className="d-flex align-items-center gap-2">
                                        {sendingReply
                                            ? <Spinner size="sm"/>
                                            : <Send size={14}/>}
                                        Send Response to {selected.email}
                                    </Button>
                                </Tab>
                            </Tabs>
                        </Modal.Body>
                    </>
                )}
            </Modal>
        </div>
    );
};
export default InquiriesPage;
